import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "react-oidc-context";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  type DateString,
  DayStringFormat,
  type Expense,
} from "../../types/presence";
import Modal from "../Modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Typography from "../Typography";
import Divider from "../Divider";
import { useUserId } from "../../hooks/useUserId";
import {
  PresenceTableName,
  useDynamoDeleteItem,
  useDynamoMutateItem,
} from "../../services/dynamo";
import {
  deleteObjectFromS3,
  getPresignedUploadUrl,
  uploadFileToS3,
} from "../../services/api";
import AttachmentItem from "./AttachmentItem";
import { UploadIcon } from "lucide-react";

const ACCEPTED_MIME = "image/*,.pdf,.doc,.docx";
type Props = {
  date: Date;
  data?: Expense;
  close: () => void;
};

type ExpenseFormData = {
  cost: number;
  description: string;
};

type UploadEntry = {
  id: string;
  name: string;
  file: File;
  key?: string;
  uploading: boolean;
  error: boolean;
};

export default function ExpenseForm({ date, data, close }: Props) {
  const { user } = useAuth();
  const userId = useUserId();
  const { mutateAsync: saveExpense } = useDynamoMutateItem<Expense>(
    PresenceTableName,
    {},
  );
  const { mutateAsync: deleteExpense } = useDynamoDeleteItem(
    PresenceTableName,
    {},
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ExpenseFormData>({
    defaultValues: {
      cost: data?.cost ?? 0,
      description: data?.description ?? "",
    },
  });

  const [uploadEntries, setUploadEntries] = useState<UploadEntry[]>([]);
  const [removedSavedKeys, setRemovedSavedKeys] = useState<Set<string>>(
    new Set(),
  );
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isUploading = uploadEntries.some((e) => e.uploading);
  const savedAttachments = (data?.attachments ?? []).filter(
    (k) => !removedSavedKeys.has(k),
  );
  const uploadedKeys = uploadEntries.filter((e) => e.key).map((e) => e.key!);
  const allAttachmentCount = savedAttachments.length + uploadedKeys.length;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";

    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    for (const file of files) {
      const id = crypto.randomUUID();
      setUploadEntries((prev) => [
        ...prev,
        { id, name: file.name, file, uploading: true, error: false },
      ]);
      try {
        const { url, key } = await getPresignedUploadUrl({
          year,
          month,
          filename: file.name,
          contentType: file.type || "application/octet-stream",
        });
        await uploadFileToS3(url, file);
        setUploadEntries((prev) =>
          prev.map((entry) =>
            entry.id === id ? { ...entry, key, uploading: false } : entry,
          ),
        );
        toast.success(`${file.name} caricato con successo`);
      } catch {
        setUploadEntries((prev) =>
          prev.map((entry) =>
            entry.id === id
              ? { ...entry, uploading: false, error: true }
              : entry,
          ),
        );
        toast.error(`Errore nel caricamento di ${file.name}`);
      }
    }
  };

  const handleRemoveUploadEntry = async (entry: UploadEntry) => {
    setUploadEntries((prev) => prev.filter((e) => e.id !== entry.id));
    if (entry.key) {
      try {
        await deleteObjectFromS3(entry.key);
      } catch {
        // silently ignore: orphaned key is not critical
      }
    }
  };

  const handleRemoveSavedAttachment = async (key: string) => {
    setRemovedSavedKeys((prev) => new Set([...prev, key]));
    toast.info(
      'Allegato rimosso. Clicca "Aggiorna" per rendere la modifica definitiva.',
    );
    try {
      await deleteObjectFromS3(key);
    } catch {
      // silently ignore
    }
  };

  const handleDelete = async () => {
    if (!data) return;
    setIsSaving(true);
    const toastId = toast.loading("Eliminazione spesa...");
    try {
      await deleteExpense({ key: { pk: data.pk, sk: data.sk } });
      toast.success("Spesa eliminata", { id: toastId });
      close();
    } catch (err) {
      console.error(err);
      toast.error("Errore durante l'eliminazione", { id: toastId });
    } finally {
      setIsSaving(false);
    }
  };

  const onSubmit = async (formData: ExpenseFormData) => {
    if (!userId || !user?.id_token) return;
    setIsSaving(true);
    const toastId = toast.loading(
      data ? "Aggiornamento spesa..." : "Salvataggio spesa...",
    );
    try {
      const skDate = format(date, DayStringFormat) as DateString;
      const sk: Expense["sk"] =
        data?.sk ?? `expense#${skDate}#${crypto.randomUUID()}`;

      await saveExpense({
        pk: userId,
        sk,
        date: skDate,
        type: "expense",
        cost: Number(formData.cost),
        description: formData.description,
        attachments: [...savedAttachments, ...uploadedKeys],
      });

      toast.success(data ? "Spesa aggiornata" : "Spesa salvata", {
        id: toastId,
      });
      close();
    } catch (err) {
      console.error(err);
      toast.error("Errore durante il salvataggio", { id: toastId });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open onClose={close} size="small">
      <div className="ExpenseForm">
        <div className="ExpenseForm__header">
          <Typography variant="h5" className="bold">
            {data ? "Modifica Spesa" : "Aggiungi Spesa"} per il{" "}
            {date.toLocaleDateString()}
          </Typography>
          <Typography variant="body2">
            Inserisci il costo totale e la descrizione delle spese.
          </Typography>
          <Divider />
        </div>

        <form id="expense-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="ExpenseForm__body">
            <div className="ExpenseForm__field">
              <Typography variant="body2">Costo</Typography>
              <div className="relative flex items-center">
                <span className="absolute left-3 text-sm text-muted-foreground pointer-events-none select-none">
                  €
                </span>
              <Input
                autoFocus
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                className="pl-7"
                disabled={isSaving}
                {...register("cost", {
                  required: "Costo richiesto",
                  setValueAs: (v: string) =>
                    parseFloat(String(v).replace(",", ".")),
                  validate: (v) =>
                    (!isNaN(v) && v > 0) ||
                    "Il costo deve essere maggiore di 0",
                })}
              />
              </div>
              {errors.cost && (
                <span className="ExpenseForm__error">
                  {errors.cost.message}
                </span>
              )}
            </div>
            <div className="ExpenseForm__field">
              <Typography variant="body2">Descrizione</Typography>
              <Input
                type="text"
                placeholder="Descrizione"
                disabled={isSaving}
                {...register("description")}
              />
              {errors.description && (
                <span className="ExpenseForm__error">
                  {errors.description.message}
                </span>
              )}
            </div>
            <div className="ExpenseForm__attachments">
              <Typography variant="body2" className="bold">
                Allegati:
              </Typography>
              {(savedAttachments.length > 0 || uploadEntries.length > 0) && (
                <ul className="ExpenseForm__attachments-list">
                  {savedAttachments.map((key) => (
                    <AttachmentItem
                      key={key}
                      name={key.split("/").pop() ?? key}
                      s3Key={key}
                      onRemove={
                        isSaving
                          ? undefined
                          : () => handleRemoveSavedAttachment(key)
                      }
                    />
                  ))}
                  {uploadEntries.map((entry) => (
                    <AttachmentItem
                      key={entry.id}
                      name={entry.name}
                      file={entry.file}
                      uploading={entry.uploading}
                      error={entry.error}
                      onRemove={
                        !entry.uploading && !isSaving
                          ? () => handleRemoveUploadEntry(entry)
                          : undefined
                      }
                    />
                  ))}
                </ul>
              )}
              {allAttachmentCount === 0 && !isUploading && (
                <>
                  <Typography variant="body2">Nessun allegato</Typography>
                  <span className="ExpenseForm__error">
                    Aggiungi almeno un allegato
                  </span>
                </>
              )}
              <div className="ExpenseForm__upload">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_MIME}
                  className="ExpenseForm__file-input"
                  onChange={handleFileChange}
                  disabled={isSaving}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSaving || isUploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <UploadIcon className="h-4 w-4 mr-1" />
                  {isUploading ? "Caricamento..." : "Scegli file"}
                </Button>
              </div>
            </div>
          </div>
        </form>

        <div className="ExpenseForm__buttons">
          {data && (
            <Button variant="ghost" disabled={isSaving} onClick={handleDelete}>
              Elimina
            </Button>
          )}
          <Button
            type="submit"
            form="expense-form"
            className="ml-auto"
            disabled={isSaving || isUploading || allAttachmentCount === 0}
          >
            {data ? "Aggiorna" : "Salva"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
